import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Link,
  Tooltip,
  Card,
  CardContent,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TimelineOutlined as TimelineIcon,
  AssignmentOutlined as AssignmentIcon,
  PersonOutline as PersonIcon,
  BusinessOutlined as BusinessIcon,
  AttachFile as AttachFileIcon,
  PriorityHigh as PriorityHighIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  SendOutlined as SendIcon,
  Add as AddIcon,
  DescriptionOutlined as DescriptionIcon,
  FormatListBulleted as ListIcon,
  InsertDriveFile as FileIcon,
  Comment as CommentIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Label as LabelIcon,
  LinkOff as LinkOffIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { base_url, getToken } from '../../config/config';

// Default colors for fallback
const defaultColors = {
  primary: {
    100: '#d0d1d5',
    200: '#a1a4ab',
    300: '#727681',
    400: '#1F2A40',
    500: '#141b2d',
  },
  grey: {
    100: '#e0e0e0',
    200: '#c2c2c2',
    300: '#a3a3a3',
    400: '#858585',
    500: '#666666',
    600: '#4d4d4d',
    700: '#333333',
    800: '#1a1a1a',
    900: '#0a0a0a',
  },
  greenAccent: {
    100: '#dbf5ee',
    200: '#b7ebde',
    300: '#94e2cd',
    400: '#70d8bd',
    500: '#4cceac',
    600: '#3da58a',
    700: '#2e7c67',
    800: '#1e5245',
    900: '#0f2922',
  },
  redAccent: {
    100: '#f8dcdb',
    200: '#f1b9b7',
    300: '#e99592',
    400: '#e2726e',
    500: '#db4f4a',
    600: '#af3f3b',
    700: '#832f2c',
    800: '#58201e',
    900: '#2c100f',
  },
  blueAccent: {
    100: '#e1e2fe',
    200: '#c3c6fd',
    300: '#a4a9fc',
    400: '#868dfb',
    500: '#6870fa',
    600: '#535ac8',
    700: '#3e4396',
    800: '#2a2d64',
    900: '#151632',
  },
  orange: {
    400: '#FB8C00',
    500: '#EF6C00',
  }
};

// Helper function to format dates
const formatDate = (dateString) => {
  try {
    return format(new Date(dateString), 'dd.MM.yyyy', { locale: de });
  } catch (error) {
    return 'Ungültiges Datum';
  }
};

// Helper function to format datetime
const formatDateTime = (dateString) => {
  try {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: de });
  } catch (error) {
    return 'Ungültiges Datum';
  }
};

const ReklamationDetail = () => {
  const { id } = useParams();
  // Fixed light mode colors - no dark mode support
  const lightModeColors = {
    primary: {
      100: '#f0f1f5',
      200: '#d6d9e0',
      300: '#a9aeb9',
      400: '#f8f9fa',
      500: '#ffffff',
    },
    grey: {
      100: '#111111',
      200: '#333333',
      300: '#555555',
      400: '#777777',
      500: '#999999',
      600: '#bbbbbb',
      700: '#dddddd',
      800: '#f5f5f5',
      900: '#ffffff',
    },
    greenAccent: {
      100: '#ebfaf5',
      200: '#d0f5eb',
      300: '#a1ebda',
      400: '#4cceac',
      500: '#3da58a',
      600: '#2e7c67',
      700: '#1e5245',
      800: '#0f2922',
      900: '#051410',
    },
    redAccent: {
      100: '#fff0f0',
      200: '#ffe0e0',
      300: '#ffc0c0',
      400: '#ff8080',
      500: '#db4f4a',
      600: '#af3f3b',
      700: '#832f2c',
      800: '#58201e',
      900: '#2c100f',
    },
    blueAccent: {
      100: '#f0f5ff',
      200: '#e0eaff',
      300: '#c0d5ff',
      400: '#80aaff',
      500: '#6870fa',
      600: '#535ac8',
      700: '#3e4396',
      800: '#2a2d64',
      900: '#151632',
    },
    orange: {
      400: '#FB8C00',
      500: '#EF6C00',
    }
  };
  
  // Use only light mode colors
  const colors = lightModeColors;
  const navigate = useNavigate();
  const token = getToken();
  
  // Initialize theme for responsiveness
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // User info
  const userState = useSelector((state) => state.user);
  const loggedInUserId = userState?.user?._id || '';
  const loggedInUserType = userState?.user?.userType || 'user';
  const username = `${userState?.user?.firstName || ''} ${
    userState?.user?.lastName || ''
  }`.trim();

  // States
  const [reklamation, setReklamation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [processes, setProcesses] = useState([]);
  const [processesLoading, setProcessesLoading] = useState(false);

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Ref for comment list to scroll to bottom on new comment
  const commentEndRef = useRef(null);

  // Fetch reklamation details on component mount
  useEffect(() => {
    fetchReklamationDetails();
    fetchProcesses();
  }, [id]);

  // Scroll to bottom of comments when new comments are added
  useEffect(() => {
    if (commentEndRef.current) {
      commentEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [reklamation?.comments]);

  // Fetch reklamation details
  const fetchReklamationDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${base_url}/v1/getReklamationById/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReklamation(response.data);
    } catch (error) {
      console.error('Error fetching reklamation details:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Reklamationsdaten',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch processes
  const fetchProcesses = async () => {
    setProcessesLoading(true);
    try {
      const response = await axios.get(`${base_url}/v1/getProcesses`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Assuming the API returns an array of processes
      if (response.data && Array.isArray(response.data)) {
        setProcesses(response.data);
      } else {
        // If API doesn't exist yet, use mock data
        setProcesses([
          { id: 'PI-001', title: 'Standardisierung der Qualitätsprüfung', link: '/processes/PI-001' },
          { id: 'PI-002', title: 'Verbesserte Kommunikation mit Kunden', link: '/processes/PI-002' },
          { id: 'PI-003', title: 'Optimierung der Materialbestellung', link: '/processes/PI-003' },
          { id: 'PI-004', title: 'Automatisierung des Dokumentationsprozesses', link: '/processes/PI-004' },
          { id: 'PI-005', title: 'Verbesserung der Fehleranalyse', link: '/processes/PI-005' },
          { id: 'PI-006', title: 'Schulungsprogramm für Qualitätssicherung', link: '/processes/PI-006' },
        ]);
      }
    } catch (error) {
      console.error('Error fetching processes:', error);
      // If API doesn't exist yet, use mock data
      setProcesses([
        { id: 'PI-001', title: 'Standardisierung der Qualitätsprüfung', link: '/processes/PI-001' },
        { id: 'PI-002', title: 'Verbesserte Kommunikation mit Kunden', link: '/processes/PI-002' },
        { id: 'PI-003', title: 'Optimierung der Materialbestellung', link: '/processes/PI-003' },
        { id: 'PI-004', title: 'Automatisierung des Dokumentationsprozesses', link: '/processes/PI-004' },
        { id: 'PI-005', title: 'Verbesserung der Fehleranalyse', link: '/processes/PI-005' },
        { id: 'PI-006', title: 'Schulungsprogramm für Qualitätssicherung', link: '/processes/PI-006' },
      ]);
    } finally {
      setProcessesLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle comment submission
  const handleCommentSubmit = async () => {
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(
        `${base_url}/v1/addComment/${id}`,
        { text: newComment },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update the reklamation with the new comment
      setReklamation({
        ...reklamation,
        comments: [...(reklamation.comments || []), response.data],
      });

      setNewComment('');
      setSnackbar({
        open: true,
        message: 'Kommentar erfolgreich hinzugefügt',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Hinzufügen des Kommentars',
        severity: 'error',
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle edit button click
  const handleEdit = () => {
    navigate(`/reklamation/edit/${id}`);
  };

  // Handle delete button click
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${base_url}/v1/deleteReklamation/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnackbar({
        open: true,
        message: 'Reklamation erfolgreich gelöscht',
        severity: 'success',
      });
      // Navigate back to reklamation list after a short delay
      setTimeout(() => {
        navigate('/reklamation');
      }, 1500);
    } catch (error) {
      console.error('Error deleting reklamation:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Löschen der Reklamation',
        severity: 'error',
      });
      setDeleting(false);
    }
  };

  // Get status icon with fixed light mode colors
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#3da58a' }} />;
      case 'in-progress':
        return <HourglassIcon sx={{ color: '#6870fa' }} />;
      case 'open':
      default:
        return <ErrorIcon sx={{ color: '#db4f4a' }} />;
    }
  };

  // Get status color with fixed light mode colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#3da58a';
      case 'in-progress':
        return '#6870fa';
      case 'open':
      default:
        return '#db4f4a';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Abgeschlossen';
      case 'in-progress':
        return 'In Bearbeitung';
      case 'open':
      default:
        return 'Offen';
    }
  };

  // Get priority color with fixed light mode colors
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#db4f4a';
      case 'medium':
        return '#EF6C00';
      case 'low':
      default:
        return '#3da58a';
    }
  };

  // Get priority label
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high':
        return 'Hoch';
      case 'medium':
        return 'Mittel';
      case 'low':
      default:
        return 'Niedrig';
    }
  };

  // Get process type color with fixed light mode colors
  const getProcessTypeColor = (type) => {
    return type === 'external' ? '#6870fa' : '#3da58a';
  };

  // Get process type label
  const getProcessTypeLabel = (type) => {
    return type === 'external' ? 'Externer Prozess' : 'Interner Prozess';
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 200px)',
        }}
      >
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!reklamation) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 200px)',
        }}
      >
        <Typography variant="h4" color={colors.grey[300]} gutterBottom>
          Reklamation nicht gefunden
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/reklamation')}
          sx={{ mt: 2 }}
        >
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ m: '20px', bgcolor: '#f8f9fa', p: 3, borderRadius: 2 }}>
      {/* HEADER */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={2}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 2 : 0}
        sx={{ 
          bgcolor: '#ffffff', 
          p: 3, 
          borderRadius: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="h2" color="#333333" fontWeight="bold" sx={{ fontSize: '1.8rem' }}>
              Reklamation {reklamation.reklamationId}
            </Typography>
            <Chip
              icon={getStatusIcon(reklamation.status)}
              label={getStatusLabel(reklamation.status)}
              sx={{
                bgcolor: getStatusColor(reklamation.status),
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                height: '28px',
              }}
            />
            <Chip
              icon={<PriorityHighIcon />}
              label={getPriorityLabel(reklamation.priority)}
              sx={{
                bgcolor: getPriorityColor(reklamation.priority),
                color: '#fff',
                fontSize: '0.9rem',
                height: '28px',
              }}
            />
            <Chip
              icon={reklamation.processType === 'external' ? <BusinessIcon /> : <PersonIcon />}
              label={getProcessTypeLabel(reklamation.processType)}
              sx={{
                bgcolor: getProcessTypeColor(reklamation.processType),
                color: '#fff',
                fontSize: '0.9rem',
                height: '28px',
              }}
            />
          </Box>
          <Typography variant="h5" color="#3da58a" sx={{ fontSize: '1rem', mt: 1 }}>
            Erstellt am {formatDate(reklamation.date)}
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={() => navigate('/reklamation')}
            startIcon={<ArrowBackIcon />}
            sx={{ 
              borderColor: '#cccccc', 
              color: '#555555',
              '&:hover': {
                borderColor: '#999999',
                bgcolor: 'rgba(0,0,0,0.02)'
              }
            }}
          >
            Zurück
          </Button>
          {/* {reklamation.status !== 'completed' && (
            <Button
              variant="contained"
              onClick={handleEdit}
              startIcon={<EditIcon />}
              sx={{
                bgcolor: '#6870fa',
                color: 'white',
                '&:hover': {
                  bgcolor: '#535ac8',
                },
              }}
            >
              Bearbeiten
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => setConfirmDialogOpen(true)}
            startIcon={<DeleteIcon />}
            disabled={deleting}
            sx={{
              bgcolor: '#db4f4a',
              color: 'white',
              '&:hover': {
                bgcolor: '#af3f3b',
              },
            }}
          >
            {deleting ? <CircularProgress size={24} /> : 'Löschen'}
          </Button> */}
        </Box>
      </Box>

      {/* TABS */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="reklamation tabs"
          textColor="secondary"
          indicatorColor="secondary"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Übersicht" />
          <Tab label="Kommunikation" />
          <Tab label="Dokumente" />
          <Tab label="Prozessverbesserungen" />
        </Tabs>
      </Box>

      {/* MAIN CONTENT - OVERVIEW TAB */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
              <Box display="flex" alignItems="center" mb={2}>
                <DescriptionIcon sx={{ mr: 1, color: colors.grey[300] }} />
                <Typography variant="h5" fontWeight="bold">
                  Beschreibung
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {reklamation.description || 'Keine Beschreibung vorhanden'}
              </Typography>
              
              {reklamation.resolutionDetails && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" alignItems="center" mb={2}>
                    <CheckCircleIcon sx={{ mr: 1, color: colors.greenAccent[400] }} />
                    <Typography variant="h6" fontWeight="bold">
                      Lösungsdetails
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {reklamation.resolutionDetails}
                  </Typography>
                </>
              )}
            </Paper>

            {reklamation.kanbanTask && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <AssignmentIcon sx={{ mr: 1, color: colors.grey[300] }} />
                  <Typography variant="h5" fontWeight="bold">
                    Verknüpfte Aufgabe
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 1, 
                    bgcolor: colors.primary[500],
                    borderLeft: `4px solid ${getStatusColor(reklamation.kanbanTask.status)}`, 
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                      {reklamation.kanbanTask.title}
                    </Typography>
                    <Chip
                      size="small"
                      label={getStatusLabel(reklamation.kanbanTask.status)}
                      sx={{
                        bgcolor: getStatusColor(reklamation.kanbanTask.status),
                        color: '#fff',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color={colors.grey[300]} sx={{ mt: 1 }}>
                    {reklamation.kanbanTask.description || 'Keine Beschreibung vorhanden'}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    sx={{ mt: 2 }}
                    onClick={() => navigate(`/aufgabe/${reklamation.kanbanTask._id}`)}
                  >
                    Aufgabe öffnen
                  </Button>
                </Box>
              </Paper>
            )}

            <Accordion 
              defaultExpanded 
              sx={{ 
                mb: 3, 
                bgcolor: colors.primary[400],
                '&.MuiAccordion-root:before': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="timeline-content"
                id="timeline-header"
              >
                <Box display="flex" alignItems="center">
                  <TimelineIcon sx={{ mr: 1, color: colors.grey[300] }} />
                  <Typography variant="h5" fontWeight="bold">
                    Zeitverlauf & Aktivitäten
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ position: 'relative', pt: 1 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '20px',
                      top: 0,
                      bottom: 0,
                      borderLeft: `2px dashed ${colors.grey[500]}`,
                      zIndex: 0,
                    }}
                  />
                  <List>
                    {/* Creation event */}
                    <ListItem
                      alignItems="flex-start"
                      sx={{ position: 'relative', pl: 4 }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '13px',
                          top: '25px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          bgcolor: colors.blueAccent[500],
                          zIndex: 1,
                        }}
                      />
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight="bold">
                            Reklamation erstellt
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color={colors.grey[400]}>
                            {formatDateTime(reklamation.createdAt)} • durch{' '}
                            {reklamation.creator?.firstName
                              ? `${reklamation.creator.firstName} ${reklamation.creator.lastName}`
                              : reklamation.creator?.name || 'Unbekannt'}
                          </Typography>
                        }
                      />
                    </ListItem>

                    {/* Status changes would be here */}
                    
                    {/* Comments */}
                    {reklamation.comments &&
                      reklamation.comments.map((comment, index) => (
                        <ListItem
                          key={index}
                          alignItems="flex-start"
                          sx={{ position: 'relative', pl: 4 }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: '13px',
                              top: '25px',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              bgcolor: colors.greenAccent[500],
                              zIndex: 1,
                            }}
                          />
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight="bold">
                                Kommentar hinzugefügt
                              </Typography>
                            }
                            secondary={
                              <React.Fragment>
                                <Typography variant="body2" color={colors.grey[400]}>
                                  {formatDateTime(comment.createdAt)} • durch{' '}
                                  {comment.authorName || 'Unbekannt'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={colors.grey[200]}
                                  sx={{ mt: 1, whiteSpace: 'pre-line' }}
                                >
                                  {comment.text}
                                </Typography>
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                      ))}

                    {/* Last updated */}
                    <ListItem
                      alignItems="flex-start"
                      sx={{ position: 'relative', pl: 4 }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '13px',
                          top: '25px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          bgcolor: colors.grey[500],
                          zIndex: 1,
                        }}
                      />
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight="bold">
                            Letzte Aktualisierung
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color={colors.grey[400]}>
                            {formatDateTime(reklamation.updatedAt)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon sx={{ mr: 1, color: colors.grey[300] }} />
                <Typography variant="h5" fontWeight="bold">
                  Beteiligungen
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color={colors.grey[400]}>
                  Erstellt von
                </Typography>
                <Typography variant="body1">
                  {reklamation.creator?.firstName
                    ? `${reklamation.creator.firstName} ${reklamation.creator.lastName}`
                    : reklamation.creator?.name || 'Unbekannt'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color={colors.grey[400]}>
                  Zugewiesen an
                </Typography>
                <Typography variant="body1">
                  {reklamation.recipient?.firstName
                    ? `${reklamation.recipient.firstName} ${reklamation.recipient.lastName}`
                    : reklamation.recipient?.name || 'Unbekannt'}
                </Typography>
              </Box>
              
              {reklamation.assignedTeam && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color={colors.grey[400]}>
                    Zuständiges Team
                  </Typography>
                  <Typography variant="body1">
                    {reklamation.assignedTeam}
                  </Typography>
                </Box>
              )}
            </Paper>

            <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon sx={{ mr: 1, color: colors.grey[300] }} />
                <Typography variant="h5" fontWeight="bold">
                  Projektdetails
                </Typography>
              </Box>
              
              {reklamation.anr ? (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color={colors.grey[400]}>
                      ANR
                    </Typography>
                    <Typography variant="body1">
                      {reklamation.anr}
                    </Typography>
                  </Box>
                  
                  {reklamation.auftragsbezeichnung && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color={colors.grey[400]}>
                        Auftragsbezeichnung
                      </Typography>
                      <Typography variant="body1">
                        {reklamation.auftragsbezeichnung}
                      </Typography>
                    </Box>
                  )}
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    sx={{ mt: 1 }}
                    onClick={() => navigate(`/anr/${reklamation.project?._id || 'detail'}?id=${reklamation.anr || ''}`)}
                  >
                    Projekt öffnen
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color={colors.grey[500]}>
                  Kein Projekt zugeordnet
                </Typography>
              )}
            </Paper>

            <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
              <Box display="flex" alignItems="center" mb={2}>
                <LabelIcon sx={{ mr: 1, color: colors.grey[300] }} />
                <Typography variant="h5" fontWeight="bold">
                  Details
                </Typography>
              </Box>
              
              {reklamation.referenceOrderNumber && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color={colors.grey[400]}>
                    Referenz Auftragsnummer
                  </Typography>
                  <Typography variant="body1">
                    {reklamation.referenceOrderNumber}
                  </Typography>
                </Box>
              )}
              
              {reklamation.referenceText && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color={colors.grey[400]}>
                    Referenz Text
                  </Typography>
                  <Typography variant="body1">
                    {reklamation.referenceText}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color={colors.grey[400]}>
                  Erstellungsdatum
                </Typography>
                <Typography variant="body1">
                  {formatDate(reklamation.date)}
                </Typography>
              </Box>
              
              {reklamation.deadline && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color={colors.grey[400]}>
                    Fälligkeitsdatum
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(reklamation.deadline)}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* COMMUNICATION TAB */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3, bgcolor: colors.primary[400] }}>
          <Box display="flex" alignItems="center" mb={3}>
            <CommentIcon sx={{ mr: 1, color: colors.grey[300] }} />
            <Typography variant="h5" fontWeight="bold">
              Kommunikation
            </Typography>
          </Box>

          <Box
            sx={{
              border: `1px solid ${colors.grey[700]}`,
              borderRadius: 1,
              bgcolor: colors.primary[500],
              mb: 3,
              maxHeight: '500px',
              overflow: 'auto',
            }}
          >
            <List>
              {reklamation.comments && reklamation.comments.length > 0 ? (
                reklamation.comments.map((comment, index) => (
                  <ListItem
                    key={index}
                    alignItems="flex-start"
                    sx={{
                      borderBottom:
                        index < reklamation.comments.length - 1
                          ? `1px solid ${colors.grey[700]}`
                          : 'none',
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: colors.blueAccent[500] }}>
                        {comment.authorName?.charAt(0) || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body1" fontWeight="bold">
                            {comment.authorName || 'Unbekannt'}
                          </Typography>
                          <Typography variant="body2" color={colors.grey[400]}>
                            {formatDateTime(comment.createdAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          color={colors.grey[200]}
                          sx={{ mt: 1, whiteSpace: 'pre-line' }}
                        >
                          {comment.text}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        color={colors.grey[400]}
                        align="center"
                      >
                        Noch keine Kommentare vorhanden
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              <div ref={commentEndRef} />
            </List>
          </Box>

          {/* Comment form */}
          <Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Neuen Kommentar hinzufügen..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: colors.grey[600],
                  },
                  '&:hover fieldset': {
                    borderColor: colors.grey[500],
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary[300],
                  },
                },
              }}
            />
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleCommentSubmit}
                disabled={!newComment.trim() || submittingComment}
                sx={{
                  bgcolor: colors.blueAccent[600],
                  '&:hover': {
                    bgcolor: colors.blueAccent[500],
                  },
                }}
              >
                {submittingComment ? 'Wird gesendet...' : 'Kommentar senden'}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* DOCUMENTS TAB */}
      {tabValue === 2 && (
        <Paper sx={{ p: 3, bgcolor: colors.primary[400] }}>
          <Box display="flex" alignItems="center" mb={3}>
            <AttachFileIcon sx={{ mr: 1, color: colors.grey[300] }} />
            <Typography variant="h5" fontWeight="bold">
              Dokumente & Anhänge
            </Typography>
          </Box>

          {reklamation.documents && reklamation.documents.length > 0 ? (
            <Grid container spacing={2}>
              {reklamation.documents.map((document, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ bgcolor: colors.primary[500] }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <FileIcon sx={{ mr: 1, color: colors.grey[300] }} />
                        <Typography variant="body1" fontWeight="bold" noWrap>
                          {document.originalName || `Dokument ${index + 1}`}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color={colors.grey[400]} noWrap>
                        {document.mimeType || 'Unbekannter Dateityp'}
                      </Typography>
                      {document.size && (
                        <Typography variant="body2" color={colors.grey[400]}>
                          Größe: {(document.size / 1024).toFixed(2)} KB
                        </Typography>
                      )}
                      <Box mt={2} display="flex" justifyContent="flex-end">
                        {document.s3Url && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            href={document.s3Url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Öffnen
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                bgcolor: colors.primary[500],
                borderRadius: 1,
              }}
            >
              <AttachFileIcon
                sx={{ fontSize: 60, color: colors.grey[500], mb: 2 }}
              />
              <Typography variant="h6" color={colors.grey[400]}>
                Keine Dokumente angehängt
              </Typography>
              <Typography variant="body2" color={colors.grey[500]} sx={{ mb: 3 }}>
                Bearbeiten Sie die Reklamation, um Dokumente hinzuzufügen
              </Typography>
              {reklamation.status !== 'completed' && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  Reklamation bearbeiten
                </Button>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* PROCESS IMPROVEMENTS TAB */}
      {tabValue === 3 && (
        <Paper sx={{ p: 3, bgcolor: colors.primary[400] }}>
          <Box display="flex" alignItems="center" mb={3}>
            <ListIcon sx={{ mr: 1, color: colors.grey[300] }} />
            <Typography variant="h5" fontWeight="bold">
              Prozessverbesserungen
            </Typography>
          </Box>

          {processesLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : reklamation.processImprovements &&
            reklamation.processImprovements.length > 0 ? (
            <Grid container spacing={2}>
              {reklamation.processImprovements.map((processId) => {
                const process = processes.find((p) => p.id === processId);
                
                return (
                  <Grid item xs={12} sm={6} key={processId}>
                    <Card sx={{ bgcolor: colors.primary[500] }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                          <AssignmentIcon sx={{ mr: 1, color: colors.blueAccent[400] }} />
                          <Typography variant="body1" fontWeight="bold">
                            {processId}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body1" mb={2}>
                          {process?.title || 'Unbekannter Prozess'}
                        </Typography>
                        
                        <Box display="flex" justifyContent="space-between">
                          {process?.link ? (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LinkIcon />}
                              href={process.link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Details anzeigen
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LinkOffIcon />}
                              disabled
                            >
                              Kein Link verfügbar
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                bgcolor: colors.primary[500],
                borderRadius: 1,
              }}
            >
              <ListIcon
                sx={{ fontSize: 60, color: colors.grey[500], mb: 2 }}
              />
              <Typography variant="h6" color={colors.grey[400]}>
                Keine Prozessverbesserungen verknüpft
              </Typography>
              <Typography variant="body2" color={colors.grey[500]} sx={{ mb: 3 }}>
                Bearbeiten Sie die Reklamation, um Prozessverbesserungen hinzuzufügen
              </Typography>
              {reklamation.status !== 'completed' && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  Reklamation bearbeiten
                </Button>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* CONFIRM DELETE DIALOG */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Reklamation löschen?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Sind Sie sicher, dass Sie diese Reklamation löschen möchten? Dieser
            Vorgang kann nicht rückgängig gemacht werden und alle zugehörigen
            Aufgaben werden ebenfalls gelöscht.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReklamationDetail;